import {
    BoilersUsageTypeEnum,
    DirectionEnum,
    EngineDataTypeEnum,
    FuelTypeEnum,
    NoonReportSectionEnum,
    NoonReportStatusEnum,
    SeaStateEnum,
    SectionRoleEnum,
    TankTypeEnum,
    WindForceEnum,
} from '../types/ViewModel/AolEnums';
import { NoonReportSectionData } from '../types/ViewModel/NoonReportSectionData';
import { Tank } from '../types/ViewModel/Vessel/Tank';
import { AolDataItem, AolGroup } from '../types/ViewModel/AolItem';
import { FuelRobPerTank } from '../types/ViewModel/FuelRobPerTank';
import { addDateDays, isDateHourAfter, isDateSame, subtractDateDays } from 'odfjell-date-time-utils/dateTimeUtils';
import { convertToDate } from 'odfjell-date-time-utils/dateTimeConverter';
import { Consumption } from 'src/types/ViewModel/Aol/Consumption';
import { VoyageFuelPrice } from 'src/types/ViewModel/VoyageFuelPrice';
import { FuelType } from 'src/types/ViewModel/Bunkering/FuelType';
import { BoilerConsumptionV5 } from 'src/types/ViewModel/Aol/BoilerConsumptionV5';
import { FuelROB } from 'src/types/ViewModel/Bunkering/FuelROB';
import { getTanksWhereBunkerRefHasROB } from 'src/components/bunkering/bunker-rob-page/RobUtils';
import { Bunkering } from 'src/types/ViewModel/Bunkering/Bunkering';
import { UUID } from 'crypto';

export const getNumberValue = (value: number | null | undefined, decimals?: number, alwaysReturnNumber?: boolean) => {
    if (value === null || value === undefined) {
        if (alwaysReturnNumber) return 0;
        return;
    }
    return decimals ? +value.toFixed(decimals) : value;
};

export const getStringValue = (value: string | null | undefined) => {
    if (value === null || value === undefined) return '';
    return value;
};

export const getBoolValue = (value: boolean | null | undefined) => {
    if (value === null || value === undefined) return;
    return value;
};

export const getListValue = (value: any[] | null | undefined) => {
    if (value === null || value === undefined) return [];
    return value;
};

export const createSectionData = (section: NoonReportSectionEnum): NoonReportSectionData => {
    return { section, isCompleted: false, isApproved: false, role: SectionRoleEnum.Captain };
};

export const convertWaveHeightToSeaState = (waveHeight: number) => {
    if (waveHeight <= 0) return getSeaStateString(SeaStateEnum.CalmGlassy);
    if (waveHeight > 0 && waveHeight <= 0.1) return getSeaStateString(SeaStateEnum.CalmRippled);
    if (waveHeight > 0.1 && waveHeight <= 0.5) return getSeaStateString(SeaStateEnum.SmoothWavelets);
    if (waveHeight > 0.5 && waveHeight <= 1.25) return getSeaStateString(SeaStateEnum.Slight);
    if (waveHeight > 1.25 && waveHeight <= 2.5) return getSeaStateString(SeaStateEnum.Moderate);
    if (waveHeight > 2.5 && waveHeight <= 4) return getSeaStateString(SeaStateEnum.Rough);
    if (waveHeight > 4 && waveHeight <= 6) return getSeaStateString(SeaStateEnum.VeryRough);
    if (waveHeight > 6 && waveHeight <= 9) return getSeaStateString(SeaStateEnum.High);
    if (waveHeight > 9 && waveHeight <= 14) return getSeaStateString(SeaStateEnum.VeryHigh);

    return getSeaStateString(SeaStateEnum.Phenomenal);
};

export const getWindForcePresentationString = (windForce: WindForceEnum) => {
    let presentationString = '';

    switch (windForce) {
        case WindForceEnum.Calm:
            presentationString = 'Calm';
            break;
        case WindForceEnum.LightAir:
            presentationString = 'Light air';
            break;
        case WindForceEnum.GentleBreeze:
            presentationString = 'Gentle breeze';
            break;
        case WindForceEnum.ModerateBreeze:
            presentationString = 'Moderate breeze';
            break;
        case WindForceEnum.FreshBreeze:
            presentationString = 'Fresh breeze';
            break;
        case WindForceEnum.StrongBreeze:
            presentationString = 'Strong breeze';
            break;
        case WindForceEnum.HighWindModerateGaleNearGale:
            presentationString = 'High wind, moderate gale, near gale';
            break;
        case WindForceEnum.GaleFreshGale:
            presentationString = 'Gale, fresh gale';
            break;
        case WindForceEnum.StrongGale:
            presentationString = 'Strong gale';
            break;
        case WindForceEnum.StormWholeGale:
            presentationString = 'Storm, whole gale';
            break;
        case WindForceEnum.ViolentStorm:
            presentationString = 'Violent storm';
            break;
        case WindForceEnum.HurricaneForce:
            presentationString = 'Hurricane force';
            break;
    }

    return `${windForce} - ${presentationString}`;
};

export const getDirectionPresentationString = (direction: DirectionEnum) => {
    let presentationString = '';

    switch (direction) {
        case DirectionEnum.Against:
            presentationString = 'Against';
            break;
        case DirectionEnum.AgainstStarboardSide:
            presentationString = 'Against starboard side';
            break;
        case DirectionEnum.StarboardSide:
            presentationString = 'Starboard side';
            break;
        case DirectionEnum.WithStarboardSide:
            presentationString = 'With starboard side';
            break;
        case DirectionEnum.With:
            presentationString = 'With';
            break;
        case DirectionEnum.WithPortSide:
            presentationString = 'With port side';
            break;
        case DirectionEnum.PortSide:
            presentationString = 'Port side';
            break;
        case DirectionEnum.AgainstPortSide:
            presentationString = 'Against port side';
            break;
    }

    return `${direction} - ${presentationString}`;
};

export const enumToArray = (myEnum, enumToStringFunction: (myEnum) => string) => {
    const stringIsNumber = (value) => !isNaN(Number(value));
    return Object.values(myEnum).filter(stringIsNumber).map(enumToStringFunction);
};

export const getSeaStateEnum = (seaStateString: string) => {
    switch (seaStateString) {
        case SeaStateEnum.CalmGlassy + ' - Calm (glassy)':
            return SeaStateEnum.CalmGlassy;
        case SeaStateEnum.CalmRippled + ' - Calm (rippled)':
            return SeaStateEnum.CalmRippled;
        case SeaStateEnum.SmoothWavelets + ' - Smooth (wavelets)':
            return SeaStateEnum.SmoothWavelets;
        case SeaStateEnum.Slight + ' - Slight':
            return SeaStateEnum.Slight;
        case SeaStateEnum.Moderate + ' - Moderate':
            return SeaStateEnum.Moderate;
        case SeaStateEnum.Rough + ' - Rough':
            return SeaStateEnum.Rough;
        case SeaStateEnum.VeryRough + ' - Very rough':
            return SeaStateEnum.VeryRough;
        case SeaStateEnum.High + ' - High':
            return SeaStateEnum.High;
        case SeaStateEnum.VeryHigh + ' - Very high':
            return SeaStateEnum.VeryHigh;
        case SeaStateEnum.Phenomenal + ' - Phenomenal':
            return SeaStateEnum.Phenomenal;
    }
};

export const getDirectionEnum = (directionString: string) => {
    switch (directionString) {
        case DirectionEnum.Against + ' - Against':
            return DirectionEnum.Against;
        case DirectionEnum.AgainstStarboardSide + ' - Against starboard side':
            return DirectionEnum.AgainstStarboardSide;
        case DirectionEnum.StarboardSide + ' - Starboard side':
            return DirectionEnum.StarboardSide;
        case DirectionEnum.WithStarboardSide + ' - With starboard side':
            return DirectionEnum.WithStarboardSide;
        case DirectionEnum.With + ' - With':
            return DirectionEnum.With;
        case DirectionEnum.WithPortSide + ' - With port side':
            return DirectionEnum.WithPortSide;
        case DirectionEnum.PortSide + ' - Port side':
            return DirectionEnum.PortSide;
        case DirectionEnum.AgainstPortSide + ' - Against port side':
            return DirectionEnum.AgainstPortSide;
    }
};

export const getWindForceEnum = (windForceString: string) => {
    switch (windForceString) {
        case WindForceEnum.Calm + ' - Calm':
            return WindForceEnum.Calm;
        case WindForceEnum.LightAir + ' - Light air':
            return WindForceEnum.LightAir;
        case WindForceEnum.GentleBreeze + ' - Gentle breeze':
            return WindForceEnum.GentleBreeze;
        case WindForceEnum.ModerateBreeze + ' - Moderate breeze':
            return WindForceEnum.ModerateBreeze;
        case WindForceEnum.FreshBreeze + ' - Fresh breeze':
            return WindForceEnum.FreshBreeze;
        case WindForceEnum.StrongBreeze + ' - Strong breeze':
            return WindForceEnum.StrongBreeze;
        case WindForceEnum.HighWindModerateGaleNearGale + ' - High wind, moderate gale, near gale':
            return WindForceEnum.HighWindModerateGaleNearGale;
        case WindForceEnum.GaleFreshGale + ' - Gale, fresh gale':
            return WindForceEnum.GaleFreshGale;
        case WindForceEnum.StrongGale + ' - Strong gale':
            return WindForceEnum.StrongGale;
        case WindForceEnum.StormWholeGale + ' - Storm, whole gale':
            return WindForceEnum.StormWholeGale;
        case WindForceEnum.ViolentStorm + ' - Violent storm':
            return WindForceEnum.ViolentStorm;
        case WindForceEnum.HurricaneForce + ' - Hurricane force':
            return WindForceEnum.HurricaneForce;
    }
};

export const hasMissingValues = (aolObject: any, ignore?: string[]) => {
    if (!aolObject || (Object.keys(aolObject).length === 0 && Object.getPrototypeOf(aolObject) === Object.prototype)) return true;
    for (const property in aolObject) {
        if (property !== 'sectionData' && !ignore?.some((i) => i === property) && Object.prototype.hasOwnProperty.call(aolObject, property)) {
            if (aolObject[property] === undefined || aolObject[property] === null) {
                return true;
            }
        }
    }

    return false;
};
export const hasInvalidFields = (vm: any, fieldName: string, isValid: boolean) => {
    if (vm.notValidFieldsArray) {
        let _notValidFieldsArray = vm.notValidFieldsArray;

        if (!_notValidFieldsArray) {
            _notValidFieldsArray = [];
        }

        if (_notValidFieldsArray.length === 0 && !isValid) {
            _notValidFieldsArray.push(fieldName);
        }

        if (_notValidFieldsArray.find((inputName) => inputName === fieldName) && isValid) {
            _notValidFieldsArray = _notValidFieldsArray.filter((inputName) => inputName !== fieldName);
        } else if (!_notValidFieldsArray.find((inputName) => inputName === fieldName) && !isValid) {
            _notValidFieldsArray.push(fieldName);
        }

        vm.notValidFieldsArray = _notValidFieldsArray;
        vm.hasInvalidFields = vm.notValidFieldsArray.length > 0;
    }
};

const getSeaStateString = (seaState: SeaStateEnum) => {
    let seaStateString = '';

    switch (seaState) {
        case SeaStateEnum.CalmGlassy:
            seaStateString = 'Calm (glassy)';
            break;
        case SeaStateEnum.CalmRippled:
            seaStateString = 'Calm (rippled)';
            break;
        case SeaStateEnum.SmoothWavelets:
            seaStateString = 'Smooth (wavelets)';
            break;
        case SeaStateEnum.Slight:
            seaStateString = 'Slight';
            break;
        case SeaStateEnum.Moderate:
            seaStateString = 'Moderate';
            break;
        case SeaStateEnum.Rough:
            seaStateString = 'Rough';
            break;
        case SeaStateEnum.VeryRough:
            seaStateString = 'Very rough';
            break;
        case SeaStateEnum.High:
            seaStateString = 'High';
            break;
        case SeaStateEnum.VeryHigh:
            seaStateString = 'Very high';
            break;
        case SeaStateEnum.Phenomenal:
            seaStateString = 'Phenomenal';
            break;
    }

    return `${seaState} - ${seaStateString}`;
};

export const GetFuelType = (tank: Tank | undefined | null) => {
    if (!tank) return FuelTypeEnum.HFO;
    const tankType = tank.tankType as TankTypeEnum;
    return tankType === TankTypeEnum.MarineGasOil || tankType === TankTypeEnum.VirtualMGOTank ? FuelTypeEnum.MGO : FuelTypeEnum.HFO;
};

export const getFuelPriceForFuelType = (fuelType: FuelType, voyageFuelPrice: VoyageFuelPrice) => {
    if (fuelType === FuelType.HFO) {
        return voyageFuelPrice?.hfoOnlyPrice ?? 0;
    } else if (fuelType === FuelType.LGO) {
        return voyageFuelPrice?.mgoPrice ?? 0;
    } else if (fuelType === FuelType.LSF) {
        return voyageFuelPrice?.lsfOnlyPrice ?? 0;
    } else if (fuelType === FuelType.BIO) {
        return voyageFuelPrice?.bioPrice ?? 0;
    }
    return 0;
};

export const getConsumptionCost = (fuelType: FuelType, fuelPrices: VoyageFuelPrice, consumption?: number) => {
    if (consumption !== undefined && consumption > 0 && fuelType !== undefined && fuelType && fuelType > 0 && fuelPrices) {
        return consumption * getFuelPriceForFuelType(fuelType, fuelPrices);
    } else return 0;
};

export const getConsumptionCostByFuelType = (consumption: Consumption, voyageFuelPrice: VoyageFuelPrice) => {
    return getConsumptionCost(consumption.fuelType, voyageFuelPrice, consumption.consumptionMt);
};

export const addCostToConsumption = (consumption: Consumption[], voyageFuelPrice: VoyageFuelPrice) => {
    return consumption?.map((c) => {
        c.cost = getConsumptionCost(c.fuelType, voyageFuelPrice, c.consumptionMt);
        return c;
    });
};

const createConsumptionEntriesForEngineTypeAndBunkering = (
    distinctBunkeringGuids: UUID[],
    consumption: Consumption[],
    selectedAolDataItem: AolDataItem,
    previousFuelRobs: FuelROB[],
    bunkerings: Bunkering[],
    engineType: EngineDataTypeEnum
) => {
    distinctBunkeringGuids.forEach((bunkeringGuid) => {
        const currentTanks = getTanksWhereBunkerRefHasROB(bunkeringGuid, previousFuelRobs, bunkerings);
        currentTanks?.forEach((tankId) => {
            const consumptionForTank = consumption.filter(
                (c) => c.bunkeringGuid === bunkeringGuid && c.tankId === tankId && c.engineDataType === engineType
            );
            const bunkering = bunkerings.find((b) => b.bunkeringGuid === bunkeringGuid);
            if (bunkering && bunkering.fuelType && (!consumptionForTank || consumptionForTank.length === 0)) {
                consumption.push({
                    consumptionId: 0,
                    noonReportId: selectedAolDataItem.noonReport?.metadata?.noonReportId ?? 0,
                    bunkeringId: 0,
                    bunkeringGuid: bunkeringGuid,
                    tankId: tankId,
                    consumptionMt: 0,
                    fuelType: bunkering.fuelType,
                    cost: 0,
                    engineDataType: engineType,
                });
            }
        });
    });
    return consumption;
};

export const initializeConsumption = (
    consumption: Consumption[],
    voyageFuelPrice: VoyageFuelPrice,
    aolGroups: AolGroup[],
    selectedAolDataItem: AolDataItem,
    fuelRobData: FuelROB[],
    bunkerings: Bunkering[]
): Consumption[] => {
    const aolDataItems = aolGroups.flatMap((a) => a.aolDataItems);
    const previousDate = subtractDateDays(selectedAolDataItem.reportDate, 1);
    const previousAol = aolDataItems.find((a) => isDateSame(a.reportDate, previousDate, 'day'));
    const previousFuelRobs = previousAol?.noonReport?.metadata?.noonReportId
        ? fuelRobData?.filter((f) => f.noonReportId === previousAol?.noonReport?.metadata?.noonReportId) ?? []
        : [];

    const newConsumption = consumption?.length > 0 ? [...consumption] : [];
    const activeEngineTypes =
        consumption?.length > 0
            ? consumption.map((c) => c.engineDataType)
            : previousAol?.noonReport?.enginesSection?.consumption?.map((c) => c.engineDataType);
    const distinctEngineTypes = Array.from(new Set(activeEngineTypes));

    distinctEngineTypes.forEach((engineType) => {
        const activeBunkeringGuids =
            consumption?.length > 0
                ? consumption.filter((c) => c.engineDataType === engineType).map((c) => c.bunkeringGuid)
                : previousAol?.noonReport?.enginesSection?.consumption?.filter((c) => c.engineDataType === engineType).map((c) => c.bunkeringGuid);
        const distinctBunkerGuids = Array.from(new Set(activeBunkeringGuids));
        newConsumption.concat(
            createConsumptionEntriesForEngineTypeAndBunkering(
                distinctBunkerGuids,
                newConsumption,
                selectedAolDataItem,
                previousFuelRobs,
                bunkerings,
                engineType
            )
        );
    });

    return addCostToConsumption(newConsumption, voyageFuelPrice);
};

export const intializeBoilersConsumptionByUsage = (consumption: Consumption[], boilersConsumption: BoilerConsumptionV5[], noonReportId?: number) => {
    if (consumption.length === 0) {
        return boilersConsumption;
    }

    const boilerConsumptionFuelTypes = boilersConsumption.map((bc) => bc.FuelTypeId);
    const consumptionFuelTypes = consumption.filter((c) => c.engineDataType === EngineDataTypeEnum.Boilers).map((c) => c.fuelType);
    const distinctFuelTypes = Array.from(new Set([...boilerConsumptionFuelTypes, ...consumptionFuelTypes]));

    return distinctFuelTypes.flatMap((fuelType) => {
        const existingBoilerConsumption = boilersConsumption.filter((bc) => bc.FuelTypeId === fuelType);
        if (!existingBoilerConsumption || existingBoilerConsumption.length === 0) {
            return Object.keys(BoilersUsageTypeEnum)
                .filter((v) => !isNaN(Number(v)))
                .map(
                    (type) =>
                        ({
                            BoilerConsumptionId: 0,
                            NoonReportId: noonReportId ?? 0,
                            BoilersUsageType: Number.parseInt(type) as BoilersUsageTypeEnum,
                            FuelTypeId: fuelType,
                            QuantityMt: 0,
                        } as BoilerConsumptionV5)
                );
        } else {
            return existingBoilerConsumption;
        }
    });
};

export const aolConstants = {
    MAX_NUMBER_OF_HOURS_WITHOUT_IDL_CROSSING: 28,
    MAX_NUMBER_OF_HOURS_WITH_IDL_CROSSING: 52,
    DEFAULT_MAX_POWER_LOGGED_OUTPUT: 300000,
    DEFAULT_MAX_POWER_OUTPUT: 1500,
};

/** A report span longer than this many hours is only valid when its window contains an IDL crossing event (type 147). */
export const MAX_AOL_SPAN_HOURS_WITHOUT_IDL_EVENT = 46;

/**
 * Live AOL span rule. `hasIdlCrossingEvent` is the window-scoped, non-deleted type 147 check done when the AOL items are
 * generated (port-log-manager.hasIdlCrossingEventInWindow). A span of 46 hours or less is always valid; a longer one needs that event.
 */
export const isAolSpanValid = (spanHours: number, hasIdlCrossingEvent: boolean): boolean =>
    !(spanHours > MAX_AOL_SPAN_HOURS_WITHOUT_IDL_EVENT) || hasIdlCrossingEvent;

export const getAolDateFromTime = (reportDate: Date) => {
    let aolDate = convertToDate(reportDate);
    if (isDateHourAfter(aolDate, 12)) {
        aolDate = addDateDays(aolDate, 1);
    }

    return aolDate;
};

export const getAolGroupStatus = (aolDataItems: AolDataItem[]) => {
    const hasEdited = aolDataItems.some((a) => a.status === NoonReportStatusEnum.Edited);
    if (hasEdited) return NoonReportStatusEnum.Edited;
    if (aolDataItems.length > 1) {
        const isAllMissing = aolDataItems.every((a) => a.status === NoonReportStatusEnum.Missing);

        if (isAllMissing) return NoonReportStatusEnum.Missing;
        const isAllCompleted = aolDataItems.every((a) => a.status === NoonReportStatusEnum.Completed);
        if (isAllCompleted) return NoonReportStatusEnum.Completed;

        const hasMissing = aolDataItems.some((a) => a.status === NoonReportStatusEnum.Missing);
        if (hasMissing) return NoonReportStatusEnum.Edited;
        const hasCompleted = aolDataItems.some((a) => a.status === NoonReportStatusEnum.Completed);
        if (hasCompleted) return NoonReportStatusEnum.Completed;
    } else {
        const hasMissing = aolDataItems.some((a) => a.status === NoonReportStatusEnum.Missing);
        const hasCompleted = aolDataItems.some((a) => a.status === NoonReportStatusEnum.Completed);
        const hasApproved = aolDataItems.some((a) => a.status === NoonReportStatusEnum.Approved);

        if (!hasEdited && hasMissing) return NoonReportStatusEnum.Missing;
        if (!hasEdited && !hasMissing && hasCompleted) return NoonReportStatusEnum.Completed;
        if (!hasEdited && !hasMissing && !hasCompleted && hasApproved) return NoonReportStatusEnum.Approved;
    }
    return NoonReportStatusEnum.Approved;
};

export const getRobValue = (fuelRobPerTank: FuelRobPerTank, unit: string): number => {
    return unit === 'm3' ? fuelRobPerTank.robInM3 : fuelRobPerTank.robInMt;
};

export const getBunkeringValue = (fuelRobPerTank: FuelRobPerTank, unit: string): number => {
    return unit === 'm3' ? fuelRobPerTank.bunkeringInM3 : fuelRobPerTank.bunkeringInMt;
};

export const getPreviousRobValue = (fuelRobPerTank: FuelRobPerTank, unit: string): number => {
    return unit === 'm3' ? fuelRobPerTank.previousRobInM3 : fuelRobPerTank.previousRobInMt;
};

export const getFuelTypeString = (fuelType: FuelTypeEnum) => {
    if (fuelType === FuelTypeEnum.HFO) return `HFO`;
    if (fuelType === FuelTypeEnum.MGO) return `MGO`;

    return '';
};
